<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :disabled="disabled"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :selected-as-card="selectedAsCard"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  >
    <template #selected-card="{ selectedOption, clearSelection }">
      <TripEntityLink
        :trip="selectedEntity || selectedOption?.entity"
        relation-card
        :removable="clearable"
        :disabled="disabled"
        @remove="clearSelection"
      />
    </template>
  </BaseSelect>
</template>

<script>
import { mapState } from "pinia";

import { useTripStore } from "@/stores/tripStore";
import { getCompanyId } from "@/utils/userUtils";

import tripsApi from "@/services/api/tripsApi";
import TripEntityLink from "@/components/Trip/TripEntityLink.vue";

export default {
  name: "TripSelect",

  components: {
    TripEntityLink,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите рейс",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить новый рейс",
    },
    noResultsText: {
      type: String,
      default: "Рейс не найден",
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
    selectedAsCard: {
      type: Boolean,
      default: false,
    },
    selectedEntity: {
      type: Object,
      default: null,
    },
  },

  emits: ["update:modelValue", "change", "select", "button-click"],

  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
    };
  },

  computed: {
    ...mapState(useTripStore, [
      "trips",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.trips.map((trip) => ({
        value: trip.id,
        label: trip.selector_name,
        entity: trip,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    orderId(newVal, oldVal) {
      if (String(newVal || "") === String(oldVal || "")) return;

      this.preselectedOption = null;
      this.isSearchActive = false;
      this.$emit("update:modelValue", null);
      this.$emit("change", null);
      this.fetchTrips();
    },

    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
          entity: option.entity,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
      this.$emit("select", option?.entity || null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      await this.fetchTrips({ "q[search]": searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      await this.fetchTrips();
    },

    async handleScrollEnd() {
      const store = useTripStore();
      const { page, pages } = store.pagination;
      if (page < pages && !store.dataIsLoading) {
        await store.loadMoreTrips();
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useTripStore();
      const exists = store.trips.find(
        (t) => String(t.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const response = await tripsApi.show(
          this.companyId,
          this.modelValue
        );
        const trip = response.data.result;
        this.preselectedOption = {
          value: trip.id,
          label: trip.selector_name,
          entity: trip,
        };
      } catch (error) {
        console.error("Ошибка при загрузке выбранного рейса:", error);
      }
    },

    async fetchTrips(extraParams = {}) {
      const store = useTripStore();
      await store.fetchTrips({
        "q[enabled]": "true",
        ...this.orderFilterParams(),
        ...extraParams,
      });
    },

    orderFilterParams() {
      if (!this.orderId) return {};

      return { "q[order_id]": this.orderId };
    },
  },

  async mounted() {
    await this.fetchTrips();
    await this.fetchPreselectedOption();
  },
};
</script>
